import { Link } from 'react-router-dom';
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver';
import {
  StyledContactBand,
  StyledContactBandInner,
  StyledContactBandTitle,
  StyledContactBandDescription,
  StyledContactBandRow,
  StyledContactBandCopy,
  StyledContactBandCta,
  StyledContactEmail,
  StyledContactEmailArrow,
  StyledContactNetworks,
  StyledContactNetworkItem,
  StyledContactNetwork,
} from './ContactBand.styles';
import { useContactBand } from './useContactBand';
import { Icon, type IconName } from '@/components/ui/Icon';
import { getContactHref, getContactTypeIcon } from '@/types/contact';

export interface ContactBandProps {
  email?: string;
  displayEmail?: string;
  redes?: ReturnType<typeof useContactBand>['redes'];
  tagline?: string;
}

export const ContactBand = ({ email, displayEmail, redes, tagline }: ContactBandProps) => {
  const derived = useContactBand();

  const destino = email ?? derived.email;
  const direccion = displayEmail ?? derived.displayEmail;
  const redesFinales = redes ?? derived.redes;
  const frase = tagline ?? derived.tagline;

  const { ref, isIntersecting } = useIntersectionObserver<HTMLElement>();

  return (
    <StyledContactBand ref={ref} id="contact-summary" aria-label="Contacto">
      <StyledContactBandInner data-testid="contact-band-inner">
        <StyledContactBandCopy>
          <StyledContactBandTitle $visible={isIntersecting}>
            ¿Tienes un proyecto?
          </StyledContactBandTitle>
          <StyledContactBandDescription $visible={isIntersecting} data-testid="contact-tagline">
            {frase}
          </StyledContactBandDescription>

          <StyledContactBandCta $visible={isIntersecting} as={Link} to="/contact">
            ir
            <Icon name="arrowRight" size={20} aria-hidden="true" />
          </StyledContactBandCta>
        </StyledContactBandCopy>

        <StyledContactBandRow $visible={isIntersecting} data-testid="contact-band-row">
          {redesFinales.length > 0 && (
            <StyledContactNetworks aria-label="Redes">
              {redesFinales.map((red) => (
                <StyledContactNetworkItem key={red.id}>
                  <StyledContactNetwork
                    href={getContactHref(red)}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={red.label}
                  >
                    <Icon name={getContactTypeIcon(red.type) as IconName} size={22} aria-hidden="true" />
                  </StyledContactNetwork>
                </StyledContactNetworkItem>
              ))}
            </StyledContactNetworks>
          )}
          <StyledContactEmail href={`mailto:${destino}`}>
            {direccion}
            <StyledContactEmailArrow aria-hidden="true" data-testid="contact-email-arrow">
              <Icon name="arrowUpRight" size={28} />
            </StyledContactEmailArrow>
          </StyledContactEmail>
        </StyledContactBandRow>
      </StyledContactBandInner>
    </StyledContactBand>
  );
};
